/**
 * Tipos da consulta de Carga de Trabalho de Fornecedores (US-1774).
 *
 * As datas trafegam no formato `YYYY-MM-DD` do contrato e são comparadas como
 * texto: o formato ISO preserva a ordem cronológica sem converter para `Date`.
 */

/**
 * Valor de `obra.situacao` para obra cancelada, como persistido no hub-core
 * (enum gravado em `obra.situacao`, VARCHAR). Ponto único de comparação na tela.
 */
export const OBRA_SITUACAO_CANCELADA = "CANCELADA";

/** Tipo de obra (ex.: 100 · Mobilização Contratual). */
export interface ObraTipo {
  id: string;
  codigo: string;
  tipo: string;
}

/** Escopo macro da obra, com o período e o tipo de obra a que pertence. */
export interface ObraEscopoMacro {
  id: string;
  escopo: string;
  inicio: string;
  fim: string;
  tipoObraId: string;
}

export interface ObraResumo {
  id: string;
  codigo: string;
  nome: string;
  situacao: string;
  embarcacao?: { id: string; nome: string; acronimo?: string | null } | null;
  /** Tipos da obra; vazio quando a API não informa. */
  tipos?: ObraTipo[];
  /** Escopos macro da obra; vazio quando a API não informa. */
  escoposMacro?: ObraEscopoMacro[];
  /** Período da obra (`AAAA-MM-DD`); ausente quando a API não informa. */
  obraInicio?: string;
  obraFim?: string;
}

export interface FornecedorResumo {
  id: string;
  codigo: string;
  nome: string;
}

/** Alocação de um fornecedor em uma obra, no agrupamento por fornecedor. */
export interface AlocacaoDoFornecedor {
  id: string;
  inicio: string;
  fim: string;
  obra: ObraResumo;
}

/** Conflito que o usuário marcou como tratado (HU 2041): identificado pelas alocações envolvidas. */
export interface FornecedorConflitoIgnorado {
  id: string;
  fornecedorId: string;
  /** `yyyy-MM-dd` */
  conflitoInicio: string;
  conflitoFim: string;
  alocacaoIds: string[];
}

export interface FornecedorAlocacoes extends FornecedorResumo {
  alocacoes: AlocacaoDoFornecedor[];
  /** Conflitos já tratados; vazio quando o bff não informa. */
  conflitosIgnorados?: FornecedorConflitoIgnorado[];
}

/** `DISPONIVEL` = "Sem alocação no período" (não confirma a disponibilidade operacional). */
export type DisponibilidadeFornecedor = "DISPONIVEL" | "INDISPONIVEL";

/** Fornecedor candidato a substituir o do conflito. */
export interface FornecedorDisponivel extends FornecedorResumo {
  disponibilidade: DisponibilidadeFornecedor;
}

export interface ListFornecedoresDisponiveisParams {
  busca?: string;
  /** Período da alocação escolhida (`yyyy-MM-dd`). */
  dataInicio: string;
  dataFim: string;
  /** Obra que terá o fornecedor trocado: a própria alocação não conta como ocupação. */
  ignorarObraId: string;
  signal?: AbortSignal;
}

export interface RemanejarFornecedorRequest {
  fornecedorSubstitutoId: string;
  justificativa: string;
}

export interface FornecedorConflitoRemanejado {
  fornecedorId: string;
  inicio: string;
  fim: string;
  alocacaoIds: string[];
}

export interface RemanejarFornecedorResult {
  alocacao: { id: string; obraId: string; fornecedorId: string; inicio: string; fim: string };
  fornecedorAnteriorId: string;
  novoConflito: boolean;
  conflitos: FornecedorConflitoRemanejado[];
}

export interface IgnorarConflitoFornecedorRequest {
  fornecedorId: string;
  conflitoInicio: string;
  conflitoFim: string;
  /** Alocações sobrepostas que formam o conflito (no mínimo 2). */
  alocacaoIds: string[];
  justificativa: string;
}

/** Alocação de um fornecedor em uma obra, no agrupamento por obra. */
export interface AlocacaoDaObra {
  id: string;
  inicio: string;
  fim: string;
  fornecedor: FornecedorResumo;
}

export interface ObraAlocacoes extends ObraResumo {
  alocacoes: AlocacaoDaObra[];
  /** Conflitos de fornecedor tratados na simulação (HU 2224); o bff não os informa. */
  conflitosIgnorados?: FornecedorConflitoIgnorado[];
}

/** "Visualizar por" da tela (a HU chama de "Agrupar por"). */
export type AgrupamentoCarga = "fornecedor" | "obra";

export interface ListAlocacoesParams {
  dataInicio: string;
  dataFim: string;
  busca?: string;
  signal?: AbortSignal;
}

export function isObraCancelada(situacao: string | null | undefined): boolean {
  return (situacao ?? "").trim().toUpperCase() === OBRA_SITUACAO_CANCELADA;
}
